import { DESTINATION_TYPE, ORDER_SOURCE } from "./constants";

/**
 * Returns only the items selected for transfer.
 */
export const getTransferItems = (selectedQuantities = {}) => {
  return Object.entries(selectedQuantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([itemId, quantity]) => ({
      itemId,
      quantity,
    }));
};

/**
 * Finds an existing invoice for the selected destination.
 */
export const findDestinationInvoice = ({ invoices = [], destination }) => {
  if (!destination) return null;

  return (
    invoices.find((invoice) => {
      switch (destination.type) {
        case DESTINATION_TYPE.TABLE:
          return invoice.tableId === destination.id;

        case DESTINATION_TYPE.ROOM:
          return invoice.roomId === destination.id;

        default:
          return false;
      }
    }) ?? null
  );
};

/**
 * Returns true if destination already has an invoice.
 */
export const hasExistingInvoice = ({ invoices = [], destination }) => {
  return Boolean(
    findDestinationInvoice({
      invoices,
      destination,
    }),
  );
};

/**
 * Validates transfer request.
 */
export const validateTransfer = ({ destination, transferItems }) => {
  if (!destination) {
    return {
      valid: false,
      message: "Please select destination.",
    };
  }

  if (!transferItems.length) {
    return {
      valid: false,
      message: "Please select at least one item.",
    };
  }

  return {
    valid: true,
  };
};

/**
 * Creates payload common for transfer API.
 */
export const buildTransferPayload = ({ destination, transferItems }) => ({
  destination,
  items: transferItems,
});

export const buildDestinations = (tables = [], rooms = [], invoices = []) => [
  ...tables.map((table) => ({
    id: table.id,
    label: table.tableNumber,
    type: DESTINATION_TYPE.TABLE,
    data: table,
    hasOrder: invoices.some((invoice) => invoice.tableId === table.id),
  })),

  ...rooms.map((room) => ({
    id: room.id,
    label: room.roomNumber,
    type: DESTINATION_TYPE.ROOM,
    data: room,
    hasOrder: invoices.some((invoice) => invoice.roomId === room.id),
  })),
];

export const filterCurrentDestination = ({ destinations = [], invoice }) => {
  if (!invoice) return destinations;

  return destinations.filter((destination) => {
    if (destination.type === DESTINATION_TYPE.TABLE) {
      return destination.id !== invoice.tableId;
    }

    if (destination.type === DESTINATION_TYPE.ROOM) {
      return destination.id !== invoice.roomId;
    }

    return true;
  });
};

export const buildTransferOrderPayload = ({
  hotelId,
  destinationInvoice,
  selectedDestination,
  transferItemCountMap,
  invoice,
  posId,
  sourceOrderId,
}) => ({
  hotelId,
  itemCountMap: transferItemCountMap,
  numberOfPeople: invoice.numberOfPeople,
  status: invoice.status,
  posId,
  customer:
    selectedDestination.type === DESTINATION_TYPE.ROOM
      ? null
      : destinationInvoice?.customerId ||
        destinationInvoice?.customer ||
        invoice.customerId ||
        invoice.customer ||
        null,
  stewardNames: invoice.stewardNames ?? [],
  orderSource:
    selectedDestination.type === DESTINATION_TYPE.TABLE
      ? ORDER_SOURCE.TABLE
      : ORDER_SOURCE.ROOM,
  deliveryPickUp: invoice.deliveryPickUp,
  tableId:
    selectedDestination.type === DESTINATION_TYPE.TABLE
      ? selectedDestination.id
      : null,
  roomId:
    selectedDestination.type === DESTINATION_TYPE.ROOM
      ? selectedDestination.id
      : null,

  isTransferred: true,
  transferredFromOrderId: sourceOrderId,
});

export const buildNewDestinationInvoicePayload = ({
  hotelId,
  selectedDestination,
  destinationInvoice,
  transferPriceBreakup,
  orderId,
  invoice,
  posId,
  totals,
}) => ({
  hotelId,
  numberOfPeople: invoice.numberOfPeople,
  customerId:
    selectedDestination.type === DESTINATION_TYPE.ROOM
      ? null
      : destinationInvoice?.customerId || invoice.customerId || null,
  priceBreakup: transferPriceBreakup,
  status: invoice.status,
  subTotal: totals.subTotal,
  total: totals.total,
  totalTax: totals.totalTax,
  posId,
  type: invoice.type,
  invoiceType: invoice.invoiceType,
  orderSource:
    selectedDestination.type === DESTINATION_TYPE.TABLE
      ? ORDER_SOURCE.TABLE
      : ORDER_SOURCE.ROOM,
  deliveryPickUp: invoice.deliveryPickUp,
  setTotalAsPerTax: invoice.setTotalAsPerTax,
  orderId,
  orders: [orderId],
  tableId:
    selectedDestination.type === DESTINATION_TYPE.TABLE
      ? selectedDestination.id
      : null,
  roomId:
    selectedDestination.type === DESTINATION_TYPE.ROOM
      ? selectedDestination.id
      : null,
});

export const buildTransferItemCountMap = ({
  itemCountMap = {},
  transferItems = [],
}) => {
  const transferMap = {};

  transferItems.forEach(({ itemId, quantity }) => {
    const existingItem = itemCountMap[itemId];

    if (!existingItem) return;

    transferMap[itemId] = {
      ...existingItem,
      count: quantity,
    };
  });

  return transferMap;
};

export const buildRemainingItemCountMap = ({
  itemCountMap = {},
  transferItems = [],
}) => {
  const remaining = { ...itemCountMap };

  transferItems.forEach(({ itemId, quantity }) => {
    if (!remaining[itemId]) return;

    const balance = remaining[itemId].count - quantity;

    if (balance <= 0) {
      delete remaining[itemId];
    } else {
      remaining[itemId] = {
        ...remaining[itemId],
        count: balance,
      };
    }
  });

  return remaining;
};
export const buildTransferPriceBreakup = ({
  priceBreakup = [],
  transferItems = [],
}) => {
  return priceBreakup.reduce((result, item) => {
    const transfer = transferItems.find(({ itemId }) => itemId === item.itemId);

    if (!transfer) return result;

    result.push({
      ...item,
      count: transfer.quantity,
    });

    return result;
  }, []);
};
export const buildRemainingPriceBreakup = ({
  priceBreakup = [],
  transferItems = [],
}) => {
  return priceBreakup.reduce((result, item) => {
    const transfer = transferItems.find(({ itemId }) => itemId === item.itemId);
    if (!transfer) {
      result.push(item);
      return result;
    }
    const balance = item.count - transfer.quantity;
    if (balance > 0) {
      result.push({
        ...item,
        count: balance,
      });
    }

    return result;
  }, []);
};
export const calculateInvoiceTotals = (priceBreakup = []) => {
  return priceBreakup.reduce(
    (accumulator, item) => {
      const amount = item.rate * item.count;

      accumulator.subTotal += amount;
      accumulator.total += amount;

      return accumulator;
    },
    {
      subTotal: 0,
      total: 0,
      totalTax: 0,
    },
  );
};

export const buildSourceOrderPayload = ({
  orderId,
  remainingItemCountMap,
  status,
}) => ({
  id: orderId,
  itemCountMap: remainingItemCountMap,
  status,
});

export const mergeInvoiceOrders = (existingOrders = [], newOrderId) => [
  ...existingOrders,
  newOrderId,
];

export const mergeInvoicePriceBreakup = (
  existingPriceBreakup = [],
  transferPriceBreakup = [],
) => [...existingPriceBreakup, ...transferPriceBreakup];

export const buildUpdatedDestinationInvoice = ({
  invoice,
  transferPriceBreakup,
}) => {
  const priceBreakup = mergeInvoicePriceBreakup(
    invoice.priceBreakup,
    transferPriceBreakup,
  );

  const totals = calculateInvoiceTotals(priceBreakup);
  return {
    ...invoice,
    priceBreakup,
    subTotal: totals.subTotal,
    total: totals.total,
    totalTax: totals.totalTax,
  };
};

/**
 * Builds payload to update the source invoice after transfer.
 */
export const buildSourceInvoicePayload = ({
  invoice,
  remainingPriceBreakup,
  totals,
}) => ({
  ...invoice,
  priceBreakup: remainingPriceBreakup,
  subTotal: totals.subTotal,
  total: totals.total,
  totalTax: totals.totalTax,
});

export const buildTransferItems = ({ itemCountMap = {}, allItems = [] }) =>
  Object.entries(itemCountMap).reduce((result, [id, value]) => {
    const item = allItems.find((i) => i.id === id);

    if (!item) return result;

    result.push({
      ...item,
      quantity: value.count,
    });

    return result;
  }, []);
